/**
 * @format
 */

import axios from 'axios';
import { SalesSummary, MonthlySummary, DashboardQueryParams } from '../types/dashboard.types';
import { DailyPremiumData } from '../types/weeklyPremium.types';

const apiUrl = process.env.REACT_APP_SIADASH_BACKEND_URL;

class DashboardService {
  /**
   * Fetch sales summary data
   * @param params Query parameters for filtering
   * @returns Sales summary data
   */
  async getSalesSummary(
    params?: DashboardQueryParams
  ): Promise<SalesSummary> {
    try {
      const token = localStorage.getItem('accessToken');
      
      const response = await axios.get<SalesSummary>(
        `${apiUrl}/api/dashboard/sales-summary`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: this.buildParams(params),
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching sales summary:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Fetch monthly summary data
   * @param params Query parameters for filtering
   * @returns Array of monthly summary data
   */
  async getMonthlySummary(
    params?: DashboardQueryParams
  ): Promise<MonthlySummary[]> {
    try {
      const token = localStorage.getItem('accessToken');
      
      const response = await axios.get<MonthlySummary[]>(
        `${apiUrl}/api/dashboard/monthly-summary`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: this.buildParams(params),
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching monthly summary:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Fetch weekly premium data
   * @param params Query parameters for filtering
   * @returns Array of daily premium data for current and previous week
   */
  async getWeeklyPremium(params?: DashboardQueryParams): Promise<DailyPremiumData[]> {
    try {
      const token = localStorage.getItem('accessToken');
      
      const response = await axios.get<DailyPremiumData[]>(
        `${apiUrl}/api/dashboard/weekly-premium`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: this.buildParams(params),
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching weekly premium:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Build query parameters, handling arrays properly
   */
  private buildParams(params?: DashboardQueryParams): Record<string, any> {
    if (!params) return {};

    const queryParams: Record<string, any> = {};

    if (params.startDate) queryParams.startDate = params.startDate;
    if (params.endDate) queryParams.endDate = params.endDate;
    if (params.officeId) queryParams.officeId = params.officeId;
    if (params.officeIds && params.officeIds.length > 0) {
      queryParams.officeIds = params.officeIds.join(',');
    }
    if (params.userId) queryParams.userId = params.userId;
    if (params.customerId) queryParams.customerId = params.customerId;
    if (params.usePreviousDates !== undefined) {
      queryParams.usePreviousDates = params.usePreviousDates ? 'true' : 'false';
    }
    if (params.request !== undefined) queryParams.request = params.request;
    if (params.status) queryParams.status = params.status;

    return queryParams;
  }
}

export const dashboardService = new DashboardService();
